import React, {
  createContext,
  useContext,
  ReactNode,
  useCallback,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type CourseCategory = {
  id: string;
  name: string;
  slug?: string | null;
  accentColor?: string | null;
};

export type Course = {
  id: string;
  title: string;
  author: string | null;
  coverUrl: string | null;
  coverImageUrl: string | null;
  iconUrl: string | null;
  iconEmoji: string | null;
  accentColor?: string | null;
  categoryId?: string | null;
  url?: string | null;
};

type DashboardDataContextValue = {
  categories: CourseCategory[];
  courses: Course[];
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  refresh: () => Promise<void>;
};

const DashboardDataContext = createContext<DashboardDataContextValue | undefined>(
  undefined
);

type DashboardDataProviderProps = {
  children: ReactNode;
};

async function fetchDashboardPayload(userId: string | undefined) {
  const categoriesPromise = supabase
    .from("course_categories")
    .select("id, name, slug, accent_color")
    .order("name", { ascending: true });

  const coursesPromise = supabase
    .from("courses")
    .select(
      "id, title, author, cover_url, cover_image_url, icon_url, accent_color, category_id, url"
    )
    .order("created_at", { ascending: false })
    .limit(50);

  const settingsPromise = userId
    ? (supabase.from("user_course_settings") as any)
        .select("course_id, cover_image_url, icon_url, icon_emoji")
        .eq("user_id", userId)
    : null;

  const [categoriesRes, coursesRes, settingsRes] = await Promise.all([
    categoriesPromise,
    coursesPromise,
    settingsPromise,
  ]);

  if (categoriesRes.error) throw categoriesRes.error;
  if (coursesRes.error) throw coursesRes.error;
  if (settingsRes?.error) throw settingsRes.error;

  const settingsMap = new Map<string, any>();
  if (settingsRes?.data) {
    for (const s of settingsRes.data) {
      settingsMap.set(s.course_id, s);
    }
  }

  const categories: CourseCategory[] =
    categoriesRes.data?.map((c: any) => ({
      id: String(c.id),
      name: c.name,
      slug: c.slug ?? null,
      accentColor: c.accent_color ?? null,
    })) ?? [];

  const courses: Course[] =
    coursesRes.data?.map((c: any) => {
      const overrides = settingsMap.get(c.id);
      return {
        id: String(c.id),
        title: c.title,
        author: c.author ?? null,
        coverUrl: c.cover_url ?? null,
        coverImageUrl: overrides?.cover_image_url ?? c.cover_image_url ?? null,
        iconUrl: overrides?.icon_url ?? c.icon_url ?? null,
        iconEmoji: overrides?.icon_emoji ?? null,
        accentColor: c.accent_color ?? null,
        categoryId: c.category_id ? String(c.category_id) : null,
        url: c.url ?? `/course/${c.id}`,
      };
    }) ?? [];

  return { categories, courses };
}

export const DashboardDataProvider: React.FC<DashboardDataProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["dashboard-data", userId],
    queryFn: () => fetchDashboardPayload(userId),
    staleTime: 5 * 60 * 1000, // 5 min — avoids refetch on remount
    gcTime: 10 * 60 * 1000,   // 10 min garbage collection
  });

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["dashboard-data", userId] });
  }, [queryClient, userId]);

  const value: DashboardDataContextValue = {
    categories: data?.categories ?? [],
    courses: data?.courses ?? [],
    isLoading,
    isError,
    errorMessage: isError ? (error as any)?.message ?? "Failed to load dashboard data." : undefined,
    refresh,
  };

  return (
    <DashboardDataContext.Provider value={value}>
      {children}
    </DashboardDataContext.Provider>
  );
};

export const useDashboardData = (): DashboardDataContextValue => {
  const ctx = useContext(DashboardDataContext);
  if (!ctx) {
    throw new Error(
      "useDashboardData must be used within a DashboardDataProvider"
    );
  }
  return ctx;
};
